import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/stores/auth'
import { authApi } from '@/api/endpoints/auth'
import { useUI } from '@/stores/ui'
import FailedLoginModal from '@/components/common/FailedLoginModal'
import AppFooter from '@/components/common/AppFooter'

function LoginPage() {
  const navigate = useNavigate()
  const login = useAuth((state) => state.login)
  const addToast = useUI((state) => state.addToast)
  
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})
  const [showFailedLoginModal, setShowFailedLoginModal] = useState(false)
  // Locked / deleted parish (Architect Page): explain instead of a generic error
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null)
  useEffect(() => {
    const m = sessionStorage.getItem('parishBlocked')
    if (m) { sessionStorage.removeItem('parishBlocked'); setBlockedMessage(m) }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setLoading(true)

    // Basic validation
    if (!email) {
      setErrors({ email: 'Email is required' })
      setLoading(false)
      return
    }
    if (!password) {
      setErrors({ password: 'Password is required' })
      setLoading(false)
      return
    }

    try {
      const response = await authApi.login({ email, password })
      login(response.user, response.access_token, response.refresh_token)
      addToast({ message: 'Login successful!', type: 'success' })
      navigate('/dashboard')
    } catch (error: any) {
      const detail = error.response?.data?.detail
      if (error.response?.status === 401) {
        // Show popup modal instead of toast
        setShowFailedLoginModal(true)
      } else if (error.response?.status === 403 && typeof detail === 'string') {
        setBlockedMessage(detail)
      } else {
        addToast({ message: 'An error occurred. Please try again.', type: 'error' })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 font-display">Catechism Cloud</h1>
          <img 
            src="/logo.png" 
            alt="Catechism Cloud Logo" 
            className="mx-auto my-4 h-24 w-24 object-contain"
          />
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6 hover-raise">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`input-field ${errors.email ? 'border-red-500' : ''}`}
              placeholder="your.email@example.com"
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Link
              to="/reset-password"
              className="text-sm text-gold-600 hover:text-gold-700 transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          <div className="text-center space-y-2 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Not yet registered?{' '}
              <Link to="/register" className="text-gold-600 hover:text-gold-700 font-medium">
                Register here if you are a parent
              </Link>
            </p>
            <p className="text-sm text-gray-600">
              Are you a Parish looking to register?{' '}
              <Link to="/subscription" className="text-gold-600 hover:text-gold-700 font-medium">
                Click here for more details
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
    <FailedLoginModal
      open={showFailedLoginModal}
      onClose={() => setShowFailedLoginModal(false)}
    />
    {blockedMessage && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setBlockedMessage(null)}>
        <div role="alertdialog" aria-label="Parish unavailable" className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4"
          onClick={(e) => e.stopPropagation()}>
          <h2 className="text-xl font-semibold mb-3 text-gray-900">Parish unavailable</h2>
          <p className="text-gray-700 mb-4">{blockedMessage}</p>
          <div className="flex justify-end">
            <button className="btn-secondary text-sm" onClick={() => setBlockedMessage(null)}>OK</button>
          </div>
        </div>
      </div>
    )}
    <AppFooter />
    </>
  )
}

export default LoginPage
